import React, { useState } from "react"
import PropTypes from "prop-types"
import { Tag, Button, Flex } from "antd"
import { EllipsisOutlined } from "@ant-design/icons"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { routes } from "router/routes"
import CustomDrawer from "ui/Drawer"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import styles from "./index.module.css"
import { spacings } from "themes/variables"

const EquipmentCard = ({ equipment, onDeactivate, onActivate, onDelete, onAssignClick }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const [actionsDrawerOpen, setActionsDrawerOpen] = useState(false)

  const handleActionClick = (action) => {
    setActionsDrawerOpen(false)
    switch (action) {
      case "edit":
        navigate(routes.guideEquipmentEditPath({ id: equipment.key }))
        break
      case "duplicate":
        navigate(routes.guideEquipmentDuplicatePath({ id: equipment.key }))
        break
      case "deactivate":
        if (equipment.is_active) {
          onDeactivate?.(equipment)
        } else {
          onActivate?.(equipment)
        }
        break
      case "delete":
        onDelete?.(equipment)
        break
      default:
        break
    }
  }

  const handleCardClick = () => {
    navigate(routes.guideEquipmentDetailsPath({ id: equipment.key }))
  }

  return (
    <Flex className={styles.card} align="center" gap="small">
      {/* Equipment Image */}
      <button className={styles.imageContainer} onClick={handleCardClick} aria-label={`View ${equipment.title} details`}>
        {typeof equipment.image === "string" ? (
          <img src={equipment.image} alt={equipment.title} className={styles.image} />
        ) : (
          <Flex className={styles.placeholderIconContainer} align="center" justify="center">
            {equipment.image}
          </Flex>
        )}
      </button>

      {/* Equipment Content */}
      <Flex vertical gap={5} className={styles.content} onClick={handleCardClick}>
        {/* Status and Category Badges */}
        <Flex align="center" gap="small" wrap="wrap">
          <Tag color={equipment.is_active ? "green" : "red"} className={styles.statusTag}>
            {equipment.is_active ? "Active" : "Deactivated"}
          </Tag>
          {equipment.category && (
            <Tag color="blue" className={styles.categoryTag}>
              {equipment.category}
            </Tag>
          )}
        </Flex>

        {/* Equipment Name and Quantity */}
        <Flex align="center" gap="small" wrap="wrap">
          <span className={styles.title}>{equipment.title}</span>
          <Tag color="blue" className={styles.qtyTag}>
            Qty: {equipment.asset_count || 0}
          </Tag>
        </Flex>

        {/* Equipment Details */}
        <Flex vertical>
          <Flex align="center" gap="small" className={styles.detailRow}>
            <span className={styles.label}>Assigned Experiences:</span>
            <button
              className={styles.assignedExperiencesValue}
              onClick={(e) => {
                e.stopPropagation()
                onAssignClick?.(equipment)
              }}
              aria-label="Assign experiences"
            >
              {Array.isArray(equipment.assigned_experiences) ? equipment.assigned_experiences.length : 0}
            </button>
          </Flex>
          <Flex align="center" gap="small" className={styles.detailRow}>
            <span className={styles.label}>{t("equipment.table.columns.max_guests")}:</span>
            <span className={styles.value}>{equipment.max_guests || 0}</span>
          </Flex>
          <Flex align="center" gap="small" className={styles.detailRow}>
            <span className={styles.label}>Upcoming Bookings:</span>
            <span className={styles.value}>{equipment.upcoming_bookings || 0}</span>
          </Flex>
        </Flex>
      </Flex>

      {/* Actions Menu */}
      <Flex align="center" justify="center" className={styles.actions}>
        <button
          className={styles.actionButton}
          aria-label="More actions"
          onClick={(e) => {
            e.stopPropagation()
            setActionsDrawerOpen(true)
          }}
        >
          <EllipsisOutlined />
        </button>
      </Flex>

      {/* Mobile Actions Drawer */}
      <CustomDrawer open={actionsDrawerOpen} setOpen={setActionsDrawerOpen} title={equipment.title}>
        <Flex vertical gap={spacings.S} className={styles.drawerActions}>
          {hasPermission(MODULES.EQUIPMENT, ACTIONS.EDIT) && (
            <Button color="primary" variant="outlined" onClick={() => handleActionClick("edit")} className={styles.drawerActionButton}>
              Edit
            </Button>
          )}
          {hasPermission(MODULES.EQUIPMENT, ACTIONS.CREATE) && (
            <Button color="primary" variant="outlined" onClick={() => handleActionClick("duplicate")} className={styles.drawerActionButton}>
              Duplicate
            </Button>
          )}
          {hasPermission(MODULES.EQUIPMENT, ACTIONS.DEACTIVATE) && (
            <Button
              color="primary"
              variant="outlined"
              onClick={() => handleActionClick("deactivate")}
              className={styles.drawerActionButton}
            >
              {equipment.is_active ? "Deactivate" : "Activate"}
            </Button>
          )}
          {hasPermission(MODULES.EQUIPMENT, ACTIONS.DELETE) && (
            <Button danger onClick={() => handleActionClick("delete")} className={styles.drawerActionButton}>
              Delete
            </Button>
          )}
        </Flex>
      </CustomDrawer>
    </Flex>
  )
}

EquipmentCard.propTypes = {
  equipment: PropTypes.shape({
    key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    image: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    is_active: PropTypes.bool,
    category: PropTypes.string,
    asset_count: PropTypes.number,
    assigned_experiences: PropTypes.array,
    upcoming_bookings: PropTypes.number,
    max_guests: PropTypes.number
  }).isRequired,
  onDeactivate: PropTypes.func,
  onActivate: PropTypes.func,
  onDelete: PropTypes.func,
  onAssignClick: PropTypes.func
}

export default React.memo(EquipmentCard)
